import { z } from 'zod';

import { buildFormSchema } from '@/utils/validation/formSchemaKit';
import {
  buildCommonValidationMessages,
  futureDateField,
  nullableOptionalString,
  nullableOptionalStringOrNumber,
  optionalArrayField,
  optionalNumberField,
  optionalString,
  requiredString,
  withMaxLength,
} from '@/utils/validation/schemaHelpers';

import type { EmploymentType } from '@/types/employmentType';
import type { Activity, TechnicalTool } from '@/types/reference';
import type { FormSchema } from '@/utils/validation/formSchemaKit';
import type { ValidationScope } from '@/utils/validation/jobProgressFields';
import type { TFunction } from 'i18next';

type JobInfoFormSchemaOptions = {
  /** Reserved for callers; company is never required on the job wizard. */
  requireCompany?: boolean;
};

export const buildJobInfoFormSchema = (
  t: TFunction,
  scope: ValidationScope = 'save',
  options: JobInfoFormSchemaOptions = {},
) => {
  // Scope/options kept for call-site compatibility; wizard fields are never required.
  void t;
  void scope;
  void options;

  return buildFormSchema({
    title: optionalString(),
    company: optionalString(),
    specialization: optionalString(),
    required_seniority: optionalString(),
    department: optionalString(),
    city: optionalString(),
    description: {
      schema: nullableOptionalString,
      isRequired: false,
    },
    short_description: {
      schema: nullableOptionalString,
      isRequired: false,
    },
  });
};

export type JobInfoFormValues = {
  title?: string;
  department?: string;
  city?: string;
  description?: string;
  short_description?: string;
  specialization?: string;
  required_seniority?: string;
  spots_available?: number | null;
  activities?: Activity[];
  bg_image?: string | null;
  job_type?: string;
  company?: string;
  problems_description?: string;
  current_projects?: string;
  tools_used?: string[] | TechnicalTool[];
  has_active_interview?: boolean;
};

export const buildJobOfferFormSchema = (
  t: TFunction,
  scope: ValidationScope = 'save',
  employmentTypes: EmploymentType[] = [],
): FormSchema => {
  // Scope/employmentTypes kept for call-site compatibility; wizard fields are never required.
  void t;
  void scope;
  void employmentTypes;

  return buildFormSchema({
    employment_type: {
      schema: z.unknown().optional(),
      isRequired: false,
    },
    compensation_currency: optionalString(),
    extras: optionalString(),
    growth_path: optionalString(),
  });
};

export const buildJobCandidateProfileFormSchema = (
  t: TFunction,
  scope: ValidationScope = 'save',
): FormSchema => {
  void scope;
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema({
    languages: {
      schema: optionalArrayField().schema,
      isRequired: false,
    },
    is_visa_available: {
      schema: z.boolean().nullable().optional(),
      isRequired: false,
    },
    working_type: {
      schema: nullableOptionalStringOrNumber,
      isRequired: false,
    },
    days_at_the_office: {
      schema: optionalNumberField(messages).schema,
      isRequired: false,
    },
    days_working_from_home: {
      schema: optionalNumberField(messages).schema,
      isRequired: false,
    },
  });
};

export const buildJobTeamFormSchema = (
  t: TFunction,
  scope: ValidationScope = 'save',
): FormSchema => {
  void scope;
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema({
    spoken_languages: {
      schema: optionalArrayField().schema,
      isRequired: false,
    },
    team_members_num: {
      schema: optionalNumberField(messages).schema,
      isRequired: false,
    },
  });
};

export const buildJobProcessFormSchema = (t: TFunction): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema({
    extra_notes: optionalString(),
    total_duration: optionalNumberField(messages),
  });
};

export const buildProposeDateFormSchema = (t: TFunction): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema({
    date_0: futureDateField(messages.pastDate),
    place_0: requiredString(messages.required),
  });
};

export const buildUnpublishJobFormSchema = (t: TFunction): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema({
    unpublished_reason: requiredString(messages.required),
  });
};

export const buildBroadcastMessageFormSchema = (t: TFunction): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema({
    message: withMaxLength(
      requiredString(messages.required),
      3000,
      messages.maxLength(3000),
    ),
  });
};

export const buildExtensionFieldsFormSchema = (
  fieldKeys: string[],
): FormSchema => {
  return buildFormSchema(
    Object.fromEntries(fieldKeys.map((key) => [key, optionalString()])),
  );
};
